import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API from "../services/API";
import { extractErrorMessage, extractStatus } from "../utils/errorMessage";

const inputClass ="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition focus:border-role-primary focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-not-allowed disabled:bg-slate-50";

const labelClass ="text-sm font-medium text-slate-700";

type TokenStatus = "idle" | "checking" | "valid" | "used" | "expired" | "invalid";

export default function ResetPassword() {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();

 const urlToken = searchParams.get("token") ||"";

 const [token, setToken] = useState(urlToken);
 const [password, setPassword] = useState("");
 const [confirmPassword, setConfirmPassword] = useState("");
 const [showPassword, setShowPassword] = useState(false);
 const [submitting, setSubmitting] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [success, setSuccess] = useState(false);
 const [tokenStatus, setTokenStatus] = useState<TokenStatus>(
 urlToken ?"checking":"idle");

 useEffect(() => {
 if (urlToken) {
 setToken(urlToken);
 }
 }, [urlToken]);

 // Validate token from URL on mount
 useEffect(() => {
 if (!urlToken) {
 setTokenStatus("idle");
 return;
 }

 let cancelled = false;

 const validate = async () => {
 setTokenStatus("checking");
 try {
 const res = await API.get("/api/auth/reset-token-status", {
 params: { token: urlToken },
 });
 const status: string = res.data?.data?.status ??"";
 if (cancelled) return;

 if (status ==="VALID") {
 setTokenStatus("valid");
 } else if (status ==="TOKEN_ALREADY_USED") {
 setTokenStatus("used");
 } else if (status ==="TOKEN_EXPIRED") {
 setTokenStatus("expired");
 } else {
 setTokenStatus("invalid");
 }
 } catch (_err: unknown) {
 if (cancelled) return;
 setTokenStatus("invalid");
 }
 };

 validate();
 return () => {
 cancelled = true;
 };
 }, [urlToken]);

 const handleSubmit = async (e: React.FormEvent) => {
 e.preventDefault();
 setError(null);

 if (!token.trim()) {
 setError("Reset token is required.");
 return;
 }
 if (!password) {
 setError("New password is required.");
 return;
 }
 if (password.length < 8) {
 setError("Password must be at least 8 characters.");
 return;
 }
 if (password !== confirmPassword) {
 setError("Passwords do not match.");
 return;
 }

 setSubmitting(true);
 try {
 await API.post("/api/auth/reset-password", {
 token: token.trim(),
 password,
 confirmPassword,
 });

 setSuccess(true);
 } catch (err: unknown) {
 console.error("ResetPassword error: ", err);
 const status = extractStatus(err);
 if (status === 400) {
 setError("Invalid or expired token. Please request a new reset link.");
 } else {
 setError(extractErrorMessage(err,"Failed to reset password. Please try again."));
 }
 } finally {
 setSubmitting(false);
 }
 };

 const renderBlockedState = (title: string, message: string) => (
 <div className="space-y-4">
 <div className="rounded-lg border border-slate-200 bg-white p-4 text-sm text-emerald-800">
 <p className="font-semibold">{title}</p>
 <p className="mt-1 text-role-primary">{message}</p>
 </div>

 <button
 type="button"onClick={() => navigate("/forgot-password")}
 className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:shadow-emerald-500/20 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-2">
 <span
 className="pointer-events-none absolute inset-0 bg-white/20 opacity-0 transition group-hover:opacity-100"aria-hidden="true"/>
 <span className="relative">Request New Reset Link</span>
 </button>

 <p className="text-center text-xs text-slate-500">
 <button
 type="button"onClick={() => navigate("/")}
 className="font-medium text-role-primary hover:underline">
 Back to Login
 </button>
 </p>
 </div>
 );

 return (
 <div className="relative min-h-screen w-full overflow-hidden bg-slate-950 text-slate-900">
 {/* Backdrop */}
 <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-emerald-50/40"/>
 <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-emerald-300/30 blur-3xl"/>
 <div className="pointer-events-none absolute -bottom-48 -left-32 h-[34rem] w-[34rem] rounded-full bg-emerald-300/30 blur-3xl"/>
 <div className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-emerald-300/20 blur-3xl"/>

 <div className="relative mx-auto flex min-h-screen max-w-6xl items-center px-4 py-10 sm:px-6 lg:px-8">
 <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-14">
 {/* LEFT — branding */}
 <aside className="relative hidden flex-col justify-between overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-500 p-10 text-white shadow-2xl lg:flex">
 <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-white/15 blur-3xl"/>
 <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full bg-emerald-300/30 blur-3xl"/>
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.18),transparent_55%)]"/>

 <div className="relative">
 <div className="flex items-center gap-3">
 <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-inset ring-white/30 backdrop-blur">
 <svg
 className="h-6 w-6"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2.5"strokeLinecap="round"strokeLinejoin="round">
 <circle cx="12"cy="12"r="9"/>
 <path d="M12 8v4l3 3"/>
 </svg>
 </div>
 <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/30 backdrop-blur">
 Time tracking
 </span>
 </div>

 <h1 className="mt-8 text-5xl font-bold leading-[1.05] tracking-tight">
 Set a new
 <br />
 <span className="bg-gradient-to-r from-emerald-100 via-emerald-100 to-white bg-clip-text text-transparent">
 password.
 </span>
 </h1>
 <p className="mt-4 max-w-md text-base leading-relaxed text-emerald-100">
 Choose a new secure password for your account.
 </p>
 </div>

 <div className="relative mt-10">
 <button
 onClick={() => navigate("/")}
 className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-inset ring-white/20 backdrop-blur transition hover:bg-white/20">
 <svg
 className="h-4 w-4"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M19 12H5M12 5l-7 7 7 7"/>
 </svg>
 Back to Login
 </button>
 </div>
 </aside>

 {/* RIGHT — form */}
 <section className="flex items-center justify-center">
 <div className="w-full max-w-md">
 <div className="rounded-3xl border border-white/60 bg-white/80 p-7 shadow-2xl backdrop-blur-xl sm:p-9">
 <header className="mb-6">
 <p className="text-[11px] font-semibold uppercase tracking-wider text-role-primary">
 Reset password
 </p>
 <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
 Choose a new password
 </h2>
 <p className="mt-1 text-sm text-slate-500">
 Enter your new password below.
 </p>
 </header>

 {error && (
 <div
 role="alert"className="mb-5 flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
 <span className="mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-rose-500"/>
 <span className="flex-1">{error}</span>
 </div>
 )}

 {success ? (
 <div className="space-y-4">
 <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800">
 <p className="font-semibold">Password reset successful!</p>
 <p className="mt-1 text-green-700">
 Your password has been changed. You can now sign in with
 your new password.
 </p>
 </div>

 <button
 type="button"onClick={() => navigate("/")}
 className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:shadow-emerald-500/20 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-2">
 <span
 className="pointer-events-none absolute inset-0 bg-white/20 opacity-0 transition group-hover:opacity-100"aria-hidden="true"/>
 <span className="relative">Go to Login</span>
 <svg
 className="relative h-4 w-4 transition group-hover:translate-x-0.5"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M5 12h14M13 5l7 7-7 7"/>
 </svg>
 </button>
 </div>
 ) : tokenStatus ==="checking"? (
 <div className="flex flex-col items-center justify-center gap-3 py-10">
 <svg
 className="h-8 w-8 animate-spin text-role-primary"viewBox="0 0 24 24"fill="none">
 <circle
 className="opacity-25"cx="12"cy="12"r="10"stroke="currentColor"strokeWidth="4"/>
 <path
 className="opacity-75"fill="currentColor"d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
 </svg>
 <p className="text-sm text-slate-500">Verifying your reset link...</p>
 </div>
 ) : tokenStatus ==="used"? (
 renderBlockedState("Link already used","This reset link has already been used. Please request a new one.")
 ) : tokenStatus ==="expired"? (
 renderBlockedState("Link expired","This reset link has expired. Please request a new one.")
 ) : tokenStatus ==="invalid"? (
 renderBlockedState("Invalid link","This reset link is invalid or corrupted. Please request a new one.")
 ) : (
 <form onSubmit={handleSubmit} noValidate className="space-y-4">
 {/* Token (only show when no token in URL) */}
 {!urlToken && (
 <div className="flex flex-col gap-1.5">
 <label htmlFor="token"className={labelClass}>
 Reset Token
 </label>
 <div className="relative">
 <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
 <svg
 className="h-4 w-4"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
 <line x1="7"y1="7"x2="7.01"y2="7"/>
 </svg>
 </span>
 <input
 id="token"type="text"autoComplete="off"spellCheck={false}
 className={`${inputClass} pl-9 font-mono text-xs`}
 placeholder="Paste your reset token"value={token}
 onChange={(e) => setToken(e.target.value)}
 disabled={submitting}
 required
 />
 </div>
 </div>
 )}

 {/* New Password */}
 <div className="flex flex-col gap-1.5">
 <label htmlFor="password"className={labelClass}>
 New Password
 </label>
 <div className="relative">
 <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
 <svg
 className="h-4 w-4"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <rect x="3"y="11"width="18"height="11"rx="2"/>
 <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
 </svg>
 </span>
 <input
 id="password"type={showPassword ?"text":"password"}
 autoComplete="new-password"className={`${inputClass} pl-9 pr-10`}
 placeholder="Min. 8 characters"value={password}
 onChange={(e) => setPassword(e.target.value)}
 disabled={submitting}
 required
 />
 <button
 type="button"onClick={() => setShowPassword((v) => !v)}
 className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-md px-1.5 py-1 text-xs font-medium text-slate-500 hover:text-role-primary"tabIndex={-1}
 >
 {showPassword ?"Hide":"Show"}
 </button>
 </div>
 </div>

 {/* Confirm Password */}
 <div className="flex flex-col gap-1.5">
 <label htmlFor="confirmPassword"className={labelClass}>
 Confirm New Password
 </label>
 <div className="relative">
 <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
 <svg
 className="h-4 w-4"viewBox="0 0 24 24"fill="none"stroke="currentColor"strokeWidth="2"strokeLinecap="round"strokeLinejoin="round">
 <rect x="3"y="11"width="18"height="11"rx="2"/>
 <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
 </svg>
 </span>
 <input
 id="confirmPassword"type={showPassword ?"text":"password"}
 autoComplete="new-password"className={`${inputClass} pl-9 pr-10`}
 placeholder="Re-enter your password"value={confirmPassword}
 onChange={(e) => setConfirmPassword(e.target.value)}
 disabled={submitting}
 required
 />
 </div>
 </div>

 <button
 type="submit"disabled={submitting}
 className="group relative mt-2 inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/20 transition hover:shadow-emerald-500/20 focus:outline-none focus:ring-2 focus:ring-role-primary focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60">
 <span
 className="pointer-events-none absolute inset-0 bg-white/20 opacity-0 transition group-hover:opacity-100"aria-hidden="true"/>
 {submitting && (
 <svg
 className="relative h-4 w-4 animate-spin"viewBox="0 0 24 24"fill="none">
 <circle
 className="opacity-25"cx="12"cy="12"r="10"stroke="currentColor"strokeWidth="4"/>
 <path
 className="opacity-75"fill="currentColor"d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
 </svg>
 )}
 <span className="relative">
 {submitting ?"Resetting...":"Reset Password"}
 </span>
 </button>

 <p className="text-center text-xs text-slate-500">
 {urlToken ? (
 <>
 Didn&apos;t get the email?{""}
 <button
 type="button"onClick={() => navigate("/forgot-password")}
 className="font-medium text-role-primary hover:underline">
 Request again
 </button>
 </>
 ) : (
 <>
 Remember your password?{""}
 <button
 type="button"onClick={() => navigate("/")}
 className="font-medium text-role-primary hover:underline">
 Back to Login
 </button>
 </>
 )}
 </p>
 </form>
 )}
 </div>
 </div>
 </section>
 </div>
 </div>
 </div>
 );
}
